/**
 * AI features. Every output is a suggestion: the app shows it, and the rider
 * decides what to do with it. Nothing is written to the build sheet, jobs or
 * service log by these.
 */
import { z } from 'zod'
import { ComponentCategory, JobPriority } from '../enums'
import { Id, Timestamp } from './common'

export const AiKind = z.enum(['split_note', 'identify_part', 'diagnose'])
export type AiKind = z.infer<typeof AiKind>

export const CreateAiSuggestionBody = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('split_note'), capture_id: Id }),
  z.object({ kind: z.literal('identify_part'), photo_id: Id }),
  z.object({
    kind: z.literal('diagnose'),
    bike_id: Id,
    symptom: z.string().trim().min(3, 'Describe what you notice.').max(1000),
  }),
])
export type CreateAiSuggestionBody = z.input<typeof CreateAiSuggestionBody>

// ─── What the models must return (validated before storing) ────────────────

export const SuggestedTodo = z.object({
  title: z
    .string()
    .min(1)
    .max(120)
    .describe('Short imperative job title, UK English, e.g. "Bleed the rear brake"'),
  details: z
    .string()
    .max(500)
    .nullable()
    .optional()
    .describe('Anything else from the note worth keeping'),
  bike_id: z
    .string()
    .nullable()
    .optional()
    .describe('Id of the bike it is about, from the list given, or null'),
  component_id: z
    .string()
    .nullable()
    .optional()
    .describe('Id of the part it is about, from the list given, or null'),
  priority: JobPriority.describe('"safety" only if riding could be dangerous'),
})
export const SplitNoteOutput = z.object({ todos: z.array(SuggestedTodo).max(10) })
export type SplitNoteOutput = z.infer<typeof SplitNoteOutput>

export const IdentifiedPart = z.object({
  category: ComponentCategory,
  brand: z.string().max(80).nullable().optional(),
  model: z.string().max(120).nullable().optional(),
  specs: z
    .record(z.string(), z.unknown())
    .describe('Standards you can see, using the keys and values listed'),
  confidence: z.number().min(0).max(1),
  reasoning: z.string().max(400).describe('What in the photo shows this'),
})
export const IdentifyPartOutput = z.object({ parts: z.array(IdentifiedPart).max(6) })
export type IdentifyPartOutput = z.infer<typeof IdentifyPartOutput>

export const LikelyCause = z.object({
  title: z.string().min(1).max(120),
  likelihood: z.enum(['high', 'medium', 'low']),
  explanation: z.string().max(600),
  check: z.string().max(400).describe('How the rider can confirm it'),
  component_id: z
    .string()
    .nullable()
    .optional()
    .describe('Id of the part involved, from the list given, or null'),
  safety: z.boolean().describe('True if the bike should not be ridden until this is checked'),
})
export const DiagnoseOutput = z.object({
  causes: z.array(LikelyCause).max(6),
  advice: z.string().max(600).nullable().optional(),
})
export type DiagnoseOutput = z.infer<typeof DiagnoseOutput>

// ─── API shapes ─────────────────────────────────────────────────────────────

export const AiSuggestion = z.object({
  id: Id,
  kind: AiKind,
  subject_type: z.enum(['capture', 'photo', 'bike']),
  subject_id: Id,
  state: z.enum(['pending', 'done', 'failed', 'unavailable']),
  /** SplitNoteOutput, IdentifyPartOutput or DiagnoseOutput, by kind. */
  output: z.unknown().nullable(),
  error: z.string().nullable(),
  created_at: Timestamp,
  completed_at: Timestamp.nullable(),
})
export type AiSuggestion = z.infer<typeof AiSuggestion>

export const AiSuggestionList = z.object({ suggestions: z.array(AiSuggestion) })

export const AiSuggestionQuery = z.object({
  subject_type: z.enum(['capture', 'photo', 'bike']),
  subject_id: Id,
})

export const AiStatus = z.object({
  /** AI is switched on and the rider's plan includes it. */
  enabled: z.boolean(),
  /** The rider's plan includes AI (false on Free once limits are enforced). */
  in_plan: z.boolean(),
  daily_limit: z.number().int(),
  used_today: z.number().int(),
})
export type AiStatus = z.infer<typeof AiStatus>
